import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { LanguageService } from './language.service';

export interface ISelectOptions {
  value: number;
  label: string;
}

const DEFAULT_SELECTED_PAGE = 1;
const DEFAULT_ITEMS_PER_PAGE_LIMIT = -1;
export const SELECT_ALL_ITEMS_VALUE = -1;
const DEFAULT_ITEMS_PER_PAGE_OPTION: ISelectOptions =   { value: SELECT_ALL_ITEMS_VALUE, label: 'all' };
  const PAGINATION_LEVELS: ISelectOptions[] = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    DEFAULT_ITEMS_PER_PAGE_OPTION];
export const SELECT_ALL = {
  en: 'All',
  de: 'Alle',
  fr: 'Tous',
  it: 'Tutto'
}

@Injectable({
  providedIn: 'root'
})
export class PaginationService {
  private projectPagination = {
    pageNumber$: new BehaviorSubject<number>(DEFAULT_SELECTED_PAGE),
    limit$: new BehaviorSubject<number>(DEFAULT_ITEMS_PER_PAGE_LIMIT),
    numberOfItems$: new BehaviorSubject(0),
    maxPagesNumber$: new BehaviorSubject<number>(1),
    paginationLevels$: new BehaviorSubject<ISelectOptions[]>(PAGINATION_LEVELS),
    itemsPerPageDisplayValue$: new BehaviorSubject<number>(DEFAULT_ITEMS_PER_PAGE_OPTION.value)
  };

  private archivedProjectsPagination = {
    pageNumber$: new BehaviorSubject<number>(DEFAULT_SELECTED_PAGE),
    limit$: new BehaviorSubject<number>(DEFAULT_ITEMS_PER_PAGE_LIMIT),
    numberOfItems$: new BehaviorSubject(0),
    maxPagesNumber$: new BehaviorSubject<number>(1),
    paginationLevels$: new BehaviorSubject<ISelectOptions[]>(PAGINATION_LEVELS),
    itemsPerPageDisplayValue$: new BehaviorSubject<number>(DEFAULT_ITEMS_PER_PAGE_OPTION.value)
  };

  private sharedProjectsPagination = {
    pageNumber$: new BehaviorSubject<number>(DEFAULT_SELECTED_PAGE),
    limit$: new BehaviorSubject<number>(DEFAULT_ITEMS_PER_PAGE_LIMIT),
    numberOfItems$: new BehaviorSubject(0),
    maxPagesNumber$: new BehaviorSubject<number>(1),
    paginationLevels$: new BehaviorSubject<ISelectOptions[]>(PAGINATION_LEVELS),
    itemsPerPageDisplayValue$: new BehaviorSubject<number>(DEFAULT_ITEMS_PER_PAGE_OPTION.value)
  };

  constructor(
    private readonly languageService: LanguageService
  ) {
    this.setMaxNumber(this.projectPagination.limit$, this.projectPagination.numberOfItems$, this.projectPagination.maxPagesNumber$,
      this.projectPagination.paginationLevels$, this.projectPagination.itemsPerPageDisplayValue$)
      .subscribe();

    this.setMaxNumber(this.archivedProjectsPagination.limit$, this.archivedProjectsPagination.numberOfItems$,
      this.archivedProjectsPagination.maxPagesNumber$, this.archivedProjectsPagination.paginationLevels$,
      this.archivedProjectsPagination.itemsPerPageDisplayValue$)
      .subscribe();

    this.setMaxNumber(this.sharedProjectsPagination.limit$, this.sharedProjectsPagination.numberOfItems$,
      this.sharedProjectsPagination.maxPagesNumber$, this.sharedProjectsPagination.paginationLevels$,
      this.sharedProjectsPagination.itemsPerPageDisplayValue$)
      .subscribe();
  }

  private setMaxNumber(limit: BehaviorSubject<number>,
                       numberOfPaginationItems: BehaviorSubject<number>,
                       maxPagesNumber: BehaviorSubject<number>,
                       paginationLevels: BehaviorSubject<ISelectOptions[]>,
                       itemsPerPageDisplayValue: BehaviorSubject<number>) {
    return combineLatest([limit, numberOfPaginationItems])
      .pipe(
        map(([limit, numberOfItems]) => {
          maxPagesNumber.next(Math.ceil(numberOfItems / limit));
          paginationLevels.next(PAGINATION_LEVELS
              .filter(level => level.value <= numberOfPaginationItems.value)
              .map(level => {
                if (level.value === SELECT_ALL_ITEMS_VALUE) {
                  return { ...level, label: SELECT_ALL[this.languageService.getCurrentLanguage()] };
                }

                return level
              })
            );

          itemsPerPageDisplayValue.next(limit === numberOfItems ? SELECT_ALL_ITEMS_VALUE : limit);
        })
      )
  }
  sharedProjectsPaginationLevels(): Observable<ISelectOptions[]> {
    return this.sharedProjectsPagination.paginationLevels$;
  }

  archivedProjectsPaginationLevels(): Observable<ISelectOptions[]> {
    return this.archivedProjectsPagination.paginationLevels$;
  }

  paginationLevels(): Observable<ISelectOptions[]> {
    return this.projectPagination.paginationLevels$;
  }

  private incrementPaginationPage(pageNumber: BehaviorSubject<number>, maxPagesNumberValue: number) {
    if (pageNumber.value > maxPagesNumberValue) {
      return;
    }
    pageNumber.next(pageNumber.value + 1);
  }

  incrementArchivedPage() {
    this.incrementPaginationPage(this.archivedProjectsPagination.pageNumber$, this.archivedProjectsPagination.maxPagesNumber$.value)
  }

  incrementSharedProjectsPage() {
    this.incrementPaginationPage(this.sharedProjectsPagination.pageNumber$, this.sharedProjectsPagination.maxPagesNumber$.value)
  }

  incrementPage() {
    this.incrementPaginationPage(this.projectPagination.pageNumber$, this.projectPagination.maxPagesNumber$.value)
  }

  private decrementPaginationPage(pageNumber: BehaviorSubject<number>) {
    if (pageNumber.value <= 1) {
      return;
    }
    pageNumber.next(pageNumber.value - 1);
  }

  decrementSharedProjectsPage() {
    this.decrementPaginationPage(this.sharedProjectsPagination.pageNumber$);
  }

  decrementArchivedProjectsPage() {
    this.decrementPaginationPage(this.archivedProjectsPagination.pageNumber$);
  }

  decrementPage() {
    this.decrementPaginationPage(this.projectPagination.pageNumber$);
  }

  private goToFirstPaginationPage(pageNumber: BehaviorSubject<number>) {
    pageNumber.next(1);
  }

  goToFirstArchivedProjectsPage() {
    this.goToFirstPaginationPage(this.archivedProjectsPagination.pageNumber$);
  }

  goToFirstSharedProjectsPage() {
    this.goToFirstPaginationPage(this.sharedProjectsPagination.pageNumber$);
  }

  goToFirstPage() {
    this.goToFirstPaginationPage(this.projectPagination.pageNumber$);
  }

  private goToLastPaginationPage(pageNumber: BehaviorSubject<number>, maxPagesNumberValue: number) {
    pageNumber.next(maxPagesNumberValue);
  }

  goToLastArchivedProjectsPage() {
    this.goToLastPaginationPage(this.archivedProjectsPagination.pageNumber$, this.archivedProjectsPagination.maxPagesNumber$.value);
  }

  goToLastSharedProjectsPage() {
    this.goToLastPaginationPage(this.sharedProjectsPagination.pageNumber$, this.sharedProjectsPagination.maxPagesNumber$.value);
  }

  goToLastPage() {
    this.goToLastPaginationPage(this.projectPagination.pageNumber$, this.projectPagination.maxPagesNumber$.value);
  }

  getSharedProjectsPageNumber(): Observable<number> {
    return this.sharedProjectsPagination.pageNumber$;
  }

  getArchivedProjectsPageNumber(): Observable<number> {
    return this.archivedProjectsPagination.pageNumber$;
  }

  getPageNumber(): Observable<number> {
    return this.projectPagination.pageNumber$;
  }

  getSharedProjectsLimitItemsPerPage(): Observable<number> {
    return this.sharedProjectsPagination.limit$;
  }

  getArchivedProjectsLimitItemsPerPage(): Observable<number> {
    return this.archivedProjectsPagination.limit$;
  }

  getLimitItemsPerPage(): Observable<number> {
    return this.projectPagination.limit$;
  }

  private updatePaginationItemsPerPage(itemsPerPage: number,
                                       pageNumber: BehaviorSubject<number>,
                                       limit: BehaviorSubject<number>,
                                       numberOfItemsValue: number) {
    if (itemsPerPage === SELECT_ALL_ITEMS_VALUE) {
      pageNumber.next(DEFAULT_SELECTED_PAGE);
      limit.next(numberOfItemsValue);
      return;
    }

    limit.next(itemsPerPage);
    pageNumber.next(1);
  }

  updateSharedProjectsItemsPerPage(itemsPerPage: number) {
    this.updatePaginationItemsPerPage(itemsPerPage,
      this.sharedProjectsPagination.pageNumber$, this.sharedProjectsPagination.limit$,
      this.sharedProjectsPagination.numberOfItems$.value);
  }

  updateArchivedProjectsItemsPerPage(itemsPerPage: number) {
    this.updatePaginationItemsPerPage(itemsPerPage,
      this.archivedProjectsPagination.pageNumber$, this.archivedProjectsPagination.limit$,
      this.archivedProjectsPagination.numberOfItems$.value);
  }

  updateItemsPerPage(itemsPerPage: number) {
    this.updatePaginationItemsPerPage(itemsPerPage,
      this.projectPagination.pageNumber$, this.projectPagination.limit$, this.projectPagination.numberOfItems$.value);
  }

  setSharedProjectsItemsNumber(itemsNumber: number) {
    this.sharedProjectsPagination.numberOfItems$.next(itemsNumber);
  }

  setArchivedProjectsItemsNumber(itemsNumber: number) {
    this.archivedProjectsPagination.numberOfItems$.next(itemsNumber);
  }

  setItemsNumber(itemsNumber: number) {
    this.projectPagination.numberOfItems$.next(itemsNumber);
  }

  getSharedProjectsMaxPagesNumber(): Observable<number> {
    return this.sharedProjectsPagination.maxPagesNumber$;
  }

  getArchivedProjectsMaxPagesNumber(): Observable<number> {
    return this.archivedProjectsPagination.maxPagesNumber$;
  }

  getMaxPagesNumber(): Observable<number> {
    return this.projectPagination.maxPagesNumber$;
  }

  getSharedProjectsItemsPerPageDisplayValue(): Observable<number> {
    return this.sharedProjectsPagination.itemsPerPageDisplayValue$;
  }

  getArchivedProjectsItemsPerPageDisplayValue(): Observable<number> {
    return this.archivedProjectsPagination.itemsPerPageDisplayValue$;
  }

  getItemsPerPageDisplayValue(): Observable<number> {
    return this.projectPagination.itemsPerPageDisplayValue$;
  }
}
